<template>
    <el-dialog
        :visible="visible"
        title="Duyệt yêu cầu & Chỉ định Người điều phối"
        width="560px"
        custom-class="ro-dialog ro-coord-approve-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="close"
        @closed="onClosed"
    >
        <div v-if="order" class="ro-coord-approve">
            <!-- Header card tóm tắt order -->
            <div class="ro-coord-approve__order">
                <div class="ro-coord-approve__order-head">
                    <span class="ro-coord-approve__code">#{{ order.id }}</span>
                    <el-tag size="mini" type="success" effect="plain">Tuyển hộ (Giáo viên)</el-tag>
                    <el-tag size="mini" type="warning" effect="dark">Chưa có Trưởng nhóm phụ trách</el-tag>
                </div>
                <div class="ro-coord-approve__title">
                    {{ order.title || order.position_title || "Yêu cầu tuyển dụng giáo viên" }}
                </div>
                <div class="ro-coord-approve__meta">
                    <span v-if="order.quantity">Số lượng: <b>{{ order.quantity }}</b></span>
                    <span v-if="order.created_by_name || (order.created_user && order.created_user.name)">
                        · Người tạo (KHSX): <b>{{ order.created_by_name || (order.created_user && order.created_user.name) }}</b>
                    </span>
                </div>
            </div>

            <!-- Cảnh báo bắt buộc chọn Người điều phối -->
            <el-alert
                title="Bắt buộc chọn Người điều phối"
                type="warning"
                description="Yêu cầu tuyển hộ này chưa có Trưởng nhóm phụ trách khi tạo. Khi HR Lead (chị Ngà) duyệt, bắt buộc phải chọn Người điều phối để thực hiện gán trưởng nhóm phụ trách sau."
                show-icon
                :closable="false"
                class="mb-3"
            />

            <el-form
                ref="formRef"
                :model="form"
                :rules="rules"
                label-position="top"
                size="small"
                class="ro-coord-approve__form"
                @submit.native.prevent="handleConfirm"
            >
                <!-- Người điều phối -->
                <el-form-item label="Người điều phối phụ trách job" prop="coordinator_id" required>
                    <el-select
                        v-model="form.coordinator_id"
                        filterable
                        clearable
                        placeholder="Chọn người điều phối..."
                        class="w-100"
                    >
                        <el-option
                            v-for="u in coordinatorOptions"
                            :key="u.id"
                            :label="userOptionLabel(u)"
                            :value="u.id"
                        >
                            <div class="ro-coord-opt">
                                <span class="ro-coord-opt__name">{{ u.name }}</span>
                                <span v-if="u.tag || u.sub" class="ro-coord-opt__sub">
                                    {{ u.tag || u.sub }}
                                </span>
                            </div>
                        </el-option>
                    </el-select>
                    <div class="ro-form-hint">
                        Mặc định gợi ý: <b>Chị Cẩm Tú</b> (Điều phối viên tuyển dụng). Người được chọn sẽ nhận job trong màn hình Điều phối để phân công Trưởng nhóm phụ trách.
                    </div>
                </el-form-item>

                <!-- Ghi chú duyệt -->
                <el-form-item label="Ghi chú khi duyệt (không bắt buộc)">
                    <el-input
                        v-model="form.note"
                        type="textarea"
                        :rows="2"
                        maxlength="500"
                        placeholder="Nhập ghi chú cho người điều phối hoặc các bên liên quan..."
                    />
                </el-form-item>
            </el-form>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button size="small" :disabled="submitting" @click="close">
                Hủy bỏ
            </el-button>
            <el-button
                size="small"
                type="success"
                icon="el-icon-check"
                :loading="submitting"
                @click="handleConfirm"
            >
                Xác nhận duyệt & Gán điều phối
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import {
    COORDINATOR_USER_ID,
    COORDINATOR_NAME,
} from "@/core/config/recruitmentOrders/recruitmentOrdersOption";
import {
    RO_GET_HR_TEAMLEADS,
} from "@/core/services/store/recruitment-orders/recruitmentOrders.module";

export default {
    name: "AssignCoordinatorApproveDialog",
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        order: {
            type: Object,
            default: null,
        },
        submitting: {
            type: Boolean,
            default: false,
        },
        hrTeamLeads: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            form: {
                coordinator_id: COORDINATOR_USER_ID,
                note: "",
            },
            extraCoordinators: [],
            rules: {
                coordinator_id: [
                    { required: true, message: "Vui lòng chọn Người điều phối", trigger: "change" },
                ],
            },
        };
    },
    computed: {
        coordinatorOptions() {
            const list = [
                {
                    id: COORDINATOR_USER_ID,
                    name: COORDINATOR_NAME || "Chị Cẩm Tú",
                    tag: "Điều phối viên chính",
                },
            ];

            // Thêm các nhân sự từ hrTeamLeads hoặc extraCoordinators nếu có
            const source = [...(this.hrTeamLeads || []), ...(this.extraCoordinators || [])];
            source.forEach((u) => {
                if (!u || !u.id) return;
                if (!list.some((item) => String(item.id) === String(u.id))) {
                    list.push({
                        id: u.id,
                        name: u.name || `User #${u.id}`,
                        sub: u.department_name || u.team_name || u.email || "",
                    });
                }
            });

            // Nếu order đang có sẵn coordinator_id thì bổ sung
            if (this.order && this.order.coordinator && this.order.coordinator.id) {
                if (!list.some((item) => String(item.id) === String(this.order.coordinator.id))) {
                    list.unshift({
                        id: this.order.coordinator.id,
                        name: this.order.coordinator.name,
                        tag: "Hiện tại",
                    });
                }
            }

            return list;
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.initForm();
            }
        },
    },
    methods: {
        initForm() {
            // Mặc định chọn coordinator hiện tại của order hoặc chị Cẩm Tú
            const currentCoordId =
                (this.order && (this.order.coordinator_id || (this.order.coordinator && this.order.coordinator.id))) ||
                COORDINATOR_USER_ID;

            this.form = {
                coordinator_id: currentCoordId ? Number(currentCoordId) : COORDINATOR_USER_ID,
                note: "",
            };

            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.clearValidate();
                }
            });

            // Tải thêm danh sách HR team nếu chưa có
            if (!this.hrTeamLeads || this.hrTeamLeads.length === 0) {
                this.$store
                    .dispatch(RO_GET_HR_TEAMLEADS)
                    .then((res) => {
                        if (Array.isArray(res)) {
                            this.extraCoordinators = res;
                        }
                    })
                    .catch(() => {});
            }
        },
        userOptionLabel(u) {
            if (!u) return "";
            const extra = u.tag || u.sub;
            return extra ? `${u.name} (${extra})` : u.name;
        },
        close() {
            this.$emit("update:visible", false);
        },
        onClosed() {
            this.form = {
                coordinator_id: COORDINATOR_USER_ID,
                note: "",
            };
        },
        handleConfirm() {
            this.$refs.formRef.validate((valid) => {
                if (!valid) return;
                this.$emit("confirm", {
                    order: this.order,
                    coordinator_id: Number(this.form.coordinator_id),
                    note: (this.form.note || "").trim(),
                });
            });
        },
    },
};
</script>

<style scoped>
.ro-coord-approve__order {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 14px;
}

.ro-coord-approve__order-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    flex-wrap: wrap;
}

.ro-coord-approve__code {
    font-weight: 700;
    color: #4f46e5;
    font-size: 13px;
}

.ro-coord-approve__title {
    font-weight: 600;
    font-size: 14px;
    color: #0f172a;
    line-height: 1.35;
    margin-bottom: 4px;
}

.ro-coord-approve__meta {
    font-size: 12.5px;
    color: #64748b;
}

.ro-coord-opt {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.ro-coord-opt__name {
    font-weight: 500;
    color: #1e293b;
}

.ro-coord-opt__sub {
    font-size: 11.5px;
    color: #4f46e5;
    background: #eef2ff;
    padding: 1px 6px;
    border-radius: 4px;
}

.ro-form-hint {
    font-size: 12px;
    color: #64748b;
    margin-top: 5px;
    line-height: 1.4;
}
</style>
